import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { plans } from "@/config/stripePlans";
import { useNoIndex } from "@/hooks/useNoIndex";
import { invokeInternal } from "@/components/internal/invoke";
import TrialAdjustDialog from "@/components/internal/TrialAdjustDialog";
import {
  formatTrialDate,
  trialIsRunning,
  trialStatusLabel,
  type TrialAccount,
} from "@/components/internal/trials";

// Internal tool: every trial Dibs staff control. Staff set how many days a pilot
// runs, or make it never expire. Nothing here touches a paying customer: the
// function refuses accounts on a plan or with Stripe billing.
const TrialAccounts = () => {
  useNoIndex();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<TrialAccount[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState<TrialAccount | null>(null);
  const [email, setEmail] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error } = await invokeInternal<{
      success?: boolean;
      accounts?: TrialAccount[];
      error?: string;
    }>("internal-trial-accounts", { action: "list" });
    if (error || !data?.accounts) {
      setLoadError(error?.message || data?.error || "Could not load trial accounts.");
      return;
    }
    setLoadError(null);
    setAccounts(data.accounts);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const lookup = async (event: FormEvent) => {
    event.preventDefault();
    setLookupBusy(true);
    setLookupError(null);
    try {
      const { data, error } = await invokeInternal<{
        success?: boolean;
        account?: TrialAccount;
        eligible?: boolean;
        reason?: string | null;
        error?: string;
      }>("internal-trial-accounts", { action: "lookup", email: email.trim() });
      if (error) {
        setLookupError(error.message);
      } else if (!data?.account) {
        setLookupError(data?.error || "No account found for that email.");
      } else if (!data.eligible) {
        setLookupError(data.reason || "This account can't go on a trial.");
      } else {
        setEditing(data.account);
      }
    } finally {
      setLookupBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-muted/30 px-4 py-10">
      <div className="mx-auto max-w-4xl space-y-6">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <header className="space-y-1">
          <h1 className="font-heading text-xl font-semibold text-foreground">Trial accounts</h1>
          <p className="text-sm text-muted-foreground">
            Set how many days a pilot runs, or make it never expire. When a trial ends, the owner
            can't open their dashboard and the AI stops replying to customers until you extend it
            or they pay.
          </p>
        </header>

        <form onSubmit={lookup} className="space-y-1.5">
          <Label htmlFor="trial-lookup-email">Start a trial for an existing account</Label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              id="trial-lookup-email"
              type="email"
              placeholder="owner@business.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setLookupError(null);
              }}
            />
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="standard-action-button neutral-action-button h-10 shrink-0"
              disabled={lookupBusy || !email.trim()}
            >
              {lookupBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Look up
            </Button>
          </div>
          {lookupError && <p className="text-sm text-destructive">{lookupError}</p>}
        </form>

        {loadError && <p className="text-sm text-destructive">{loadError}</p>}

        {accounts === null && !loadError ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading trial accounts…
          </div>
        ) : accounts !== null ? (
          <div className="admin-data-table-shell">
            <div className="admin-data-table-scroll w-full">
              <Table className="admin-data-table min-w-[800px]">
                <caption className="sr-only">Trial accounts</caption>
                <TableHeader className="admin-data-table-header">
                  <TableRow className="admin-data-table-header-row">
                    <TableHead scope="col" className="w-[180px] px-3">Business</TableHead>
                    <TableHead scope="col" className="w-[240px] px-3">Owner email</TableHead>
                    <TableHead scope="col" className="w-[90px] px-3">Plan</TableHead>
                    <TableHead scope="col" className="w-[120px] px-3">Status</TableHead>
                    <TableHead scope="col" className="w-[110px] px-3">Ends</TableHead>
                    <TableHead scope="col" className="w-[110px] px-3 text-right">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody className="admin-data-table-body">
                  {accounts.length === 0 ? (
                    <TableRow className="admin-data-table-row">
                      <TableCell colSpan={6} className="admin-data-table-cell text-center text-muted-foreground">
                        No trial accounts yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts.map((account) => (
                      <TableRow key={account.business_id} className="admin-data-table-row">
                        <TableCell className="admin-data-table-cell truncate font-medium">
                          {account.business_name || "Unnamed business"}
                        </TableCell>
                        <TableCell className="admin-data-table-cell truncate" title={account.email ?? undefined}>
                          {account.email}
                        </TableCell>
                        <TableCell className="admin-data-table-cell">
                          {trialIsRunning(account)
                            ? plans[account.subscription_tier ?? ""]?.name ?? account.subscription_tier
                            : "—"}
                        </TableCell>
                        <TableCell className="admin-data-table-cell">
                          <Badge variant="outline" className="whitespace-nowrap">{trialStatusLabel(account)}</Badge>
                        </TableCell>
                        <TableCell className="admin-data-table-cell whitespace-nowrap tabular-nums">
                          {trialIsRunning(account) && account.next_billing_date
                            ? formatTrialDate(account.next_billing_date)
                            : "—"}
                        </TableCell>
                        <TableCell className="admin-data-table-cell text-right">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="compact-action-button neutral-action-button"
                            onClick={() => setEditing(account)}
                          >
                            {trialIsRunning(account) ? "Adjust" : "Start trial"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
      </div>

      <TrialAdjustDialog
        account={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          setEmail("");
          void load();
        }}
      />
    </main>
  );
};

export default TrialAccounts;
